// The agent workspace (21 Oct 2026).
//
// Jacky: "an agent chat view that I can select the opportunity or job, and freely discuss in turns."
// So this is a conversation, not a form: the record is pinned at the top, every ask carries it,
// and the thread keeps her tools, her words and the conversation list. The decision/communications
// desk that used to live at /emma is still at /emma/work.

import {useEffect,useRef,useState} from 'react';
import {createPortal} from 'react-dom';
import {Link} from 'react-router-dom';
import {Button} from '@aiw/ui';
import {Page} from '../shell/Page';
import {inputCls} from '../components/job-bits';
import {authHeaders,fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {
  getEmmaSession,setEmmaSession,loadConversations,loadEmmaHistory,parseChatFrame,splitFrames,quickReplies,
  type Conversation,type HistoryTurn,
} from '../lib/emma-chat';
import {
  contextFromHistory,contextLabel,messageWithContext,turnsFromHistory,
  type ChatTurn,type ContextKind,type SelectedContext,
} from '../lib/emma-context';

type Option = {ref:string;label:string;sub:string;leadId?:string|null;jobRef?:string|null};

export function EmmaChat(){
  const [conversations,setConversations]=useState<Conversation[]>([]);
  const [session,setSession]=useState<string|null>(()=>getEmmaSession());
  const [turns,setTurns]=useState<ChatTurn[]>([]);
  const [draft,setDraft]=useState('');
  const [busy,setBusy]=useState(false);
  const [failure,setFailure]=useState<string|null>(null);
  const [historyLoading,setHistoryLoading]=useState(false);
  const [context,setContext]=useState<SelectedContext|null>(null);
  const [picker,setPicker]=useState(false);
  const end=useRef<HTMLDivElement>(null);

  async function refreshConversations(){
    try{const h=await authHeaders();setConversations(await loadConversations(h,50));}catch{/* the thread can still work */}
  }
  async function openConversation(id:string|null){
    setSession(id);setEmmaSession(id);setFailure(null);setTurns([]);
    if(!id)return;
    setHistoryLoading(true);
    try{
      const h=await authHeaders();
      const history=await loadEmmaHistory(id,h,100);
      setContext(contextFromHistory(history));
      setTurns(turnsFromHistory(history));
    }catch(e){setFailure(e instanceof Error?e.message:'History unavailable');}
    finally{setHistoryLoading(false);}
  }

  useEffect(()=>{
    void (async()=>{
      try{
        const h=await authHeaders();
        const list=await loadConversations(h,50);
        setConversations(list);
        const stored=session;
        const id=stored&&list.some(c=>c.id===stored)?stored:list.find(c=>c.turnCount>0)?.id??list[0]?.id??null;
        if(id)await openConversation(id);
      }catch{/* leave an empty new conversation */}
    })();
    // one open; conversation changes are explicit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  useEffect(()=>{end.current?.scrollIntoView({behavior:'smooth',block:'end'});},[turns,busy]);

  async function send(override?:string){
    const text=(override??draft).trim();
    if(!text||busy)return;
    const sentIn=session;
    setDraft('');setFailure(null);setBusy(true);
    setTurns(all=>[...all,{role:'you',text},{role:'emma',text:'',steps:[]}]);
    const patch=(fn:(turn:ChatTurn)=>ChatTurn)=>setTurns(all=>all.map((t,i)=>i===all.length-1?fn(t):t));
    try{
      const history=turns.slice(-8).map(t=>({role:t.role==='you'?'user':'assistant',content:t.text})).filter(t=>t.content.trim());
      const response=await fetch('/emma-chat/',{
        method:'POST',
        headers:{...await authHeaders(),'content-type':'application/json',accept:'text/event-stream'},
        body:JSON.stringify({message:messageWithContext(text,context),history,sessionId:session??undefined,surface:'crm'}),
      });
      if(!response.ok||!response.body)throw Error(response.status===401?'Sign in again to talk to EmmA.':`EmmA did not answer (${response.status}).`);
      const reader=response.body.getReader(),decoder=new TextDecoder();
      let buffer='',answered=false,stopped:string|null=null;
      for(;;){
        const {value,done}=await reader.read();
        if(done)break;
        buffer+=decoder.decode(value,{stream:true});
        const {frames,rest}=splitFrames(buffer);buffer=rest;
        for(const frame of frames){
          const event=parseChatFrame(frame);
          if(!event)continue;
          if(event.type==='session'){
            const id=String(event.sessionId??'');
            if(id){setSession(id);setEmmaSession(id);}
            continue;
          }
          if(event.type==='branched'){
            const id=String(event.sessionId??'');
            if(id){setSession(id);setEmmaSession(id);}
            continue;
          }
          if(event.type==='done'){
            answered=true;
            patch(t=>({...t,text:event.result.reply?.trim()||'She had no words for that one.',model:event.result.model??null}));
            continue;
          }
          if(event.type==='error'){stopped=String(event.message??'').trim()||'EmmA stopped.';continue;}
          if(event.type==='tool'){
            const name=String((event as {name?:string;tool?:string}).name??(event as {tool?:string}).tool??'');
            if(name)patch(t=>({...t,steps:[...(t.steps??[]),name]}));
          }
        }
      }
      if(!answered){
        const why=stopped??'EmmA stopped before answering.';
        setFailure(why);patch(t=>({...t,text:t.text||why,error:why}));
      }
      if(sentIn===null)void refreshConversations();
    }catch(e){
      const why=e instanceof Error?e.message:'EmmA is unreachable.';
      setFailure(why);patch(t=>({...t,text:t.text||why,error:why}));
    }finally{setBusy(false);void refreshConversations();}
  }

  const activeTitle=conversations.find(c=>c.id===session)?.title??'New conversation';

  return <Page title="EmmA" lede="Choose an opportunity or job, then talk it through." actions={<>
    <Link className="text-sm text-primary underline" to="/emma/work">Decisions &amp; work</Link>
    <Button size="sm" variant="outline" onClick={()=>{void openConversation(null);setDraft('');}}>New conversation</Button>
  </>}>
    <div className="flex h-[calc(100vh-11.5rem)] min-h-[560px] overflow-hidden rounded-lg border bg-card">
      <aside className="hidden w-64 shrink-0 flex-col border-r bg-muted/30 md:flex">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <h2 className="text-sm font-semibold">Conversations</h2>
          <button className="text-sm text-primary underline" onClick={()=>void refreshConversations()}>Refresh</button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {conversations.length===0&&<p className="p-2 text-sm text-muted-foreground">No conversations yet. Ask her something.</p>}
          {conversations.map(c=><button key={c.id} onClick={()=>void openConversation(c.id)}
            className={`mb-1 block w-full rounded-md px-2.5 py-2 text-left text-sm ${c.id===session?'bg-primary text-primary-foreground':'hover:bg-muted'}`}>
            <span className="block truncate font-medium">{c.title||'Untitled conversation'}</span>
            <span className={`block text-micro ${c.id===session?'text-primary-foreground/70':'text-muted-foreground'}`}>{c.turnCount} turn{c.turnCount===1?'':'s'}{c.lastTurnAt?` · ${new Date(c.lastTurnAt).toLocaleDateString()}`:''}</span>
          </button>)}
        </div>
        <div className="border-t p-2">
          <Link to="/emma/work" className="block rounded-md px-2.5 py-2 text-sm text-primary underline">Needs a decision / work</Link>
        </div>
      </aside>

      <section className="flex min-w-0 flex-1 flex-col">
        <header className="flex flex-wrap items-start justify-between gap-2 border-b px-4 py-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={()=>setPicker(true)} className="rounded-full border px-3 py-1 text-sm font-medium hover:bg-muted" aria-haspopup="dialog">
                {context?contextLabel(context):'Choose opportunity or job'}
              </button>
              {context&&<button className="text-sm text-muted-foreground underline" onClick={()=>setContext(null)}>clear</button>}
            </div>
            <h2 className="mt-1 truncate font-display text-lg font-semibold">{activeTitle}</h2>
            {context?.proposalId&&<p className="text-micro text-muted-foreground">Linked Proposal {context.proposalTitle??context.proposalId.slice(0,8)}</p>}
          </div>
          <span className="text-micro text-muted-foreground">{session?`Conversation ${session.slice(0,8)}`:'New thread'}</span>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
          {historyLoading&&<p className="text-sm text-muted-foreground">Reading the conversation…</p>}
          {!historyLoading&&turns.length===0&&<div className="mx-auto max-w-xl rounded-lg border bg-muted/30 p-5 text-sm">
            <p className="font-medium">Pick an opportunity or job above, then ask anything.</p>
            <p className="mt-1 text-muted-foreground">She keeps the record in context for every turn. You can ask what is missing, what changed, what to send, or to draft the next version.</p>
            {failure&&<p className="mt-3 text-red-700" role="alert">{failure}</p>}
          </div>}
          <div className="mx-auto flex max-w-3xl flex-col gap-4">
            {turns.map((t,i)=>{
              const qr=t.role==='emma'&&t.text?quickReplies(t.text):null;
              return <div key={i} className={t.role==='you'?'ml-auto max-w-[85%]':'max-w-[90%]'}>
                {t.role==='you'
                  ?<div className="rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-sm text-primary-foreground whitespace-pre-wrap break-words">{t.text}</div>
                  :<div className={`rounded-2xl rounded-bl-sm border px-4 py-3 text-sm ${t.error?'border-red-300 bg-red-50 text-red-900':'bg-muted/40'}`}>
                    <div className="mb-1 flex items-center gap-2 text-micro font-semibold uppercase tracking-wide text-muted-foreground"><span>EmmA</span>{t.model&&<span className="normal-case tracking-normal">· {t.model}</span>}</div>
                    <p className="whitespace-pre-wrap break-words">{qr?.text??t.text??(busy&&i===turns.length-1?'Working…':'')}</p>
                    {!!t.steps?.length&&<details className="mt-2"><summary className="cursor-pointer text-micro text-muted-foreground">How I got there · {t.steps.length} step{t.steps.length===1?'':'s'}</summary><div className="mt-1 flex flex-wrap gap-1">{t.steps.map((s,j)=><span key={`${s}-${j}`} className="rounded bg-background px-1.5 py-0.5 font-mono text-micro">{s}</span>)}</div></details>}
                    {qr&&qr.replies.length>0&&<div className="mt-2 flex flex-wrap gap-1.5">{qr.replies.map(r=><button key={r} onClick={()=>void send(r)} disabled={busy} className="rounded-full border bg-background px-2.5 py-1 text-xs hover:bg-muted">{r}</button>)}</div>}
                  </div>}
              </div>;
            })}
            <div ref={end}/>
          </div>
        </div>

        <footer className="border-t p-3">
          {failure&&turns.length>0&&<p className="mb-2 text-sm text-red-700" role="alert">{failure}</p>}
          <div className="mx-auto flex max-w-3xl items-end gap-2">
            <textarea className={`${inputCls} min-h-[52px] flex-1 resize-y`} rows={2} value={draft} disabled={busy}
              onChange={e=>setDraft(e.target.value)}
              onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();void send();}}}
              placeholder={context?'Ask about this record, or tell her what to do…':'Ask EmmA anything — pin an opportunity or job above to give her the record.'}/>
            <Button disabled={busy||!draft.trim()} onClick={()=>void send()}>{busy?'Working…':'Send'}</Button>
          </div>
          <p className="mx-auto mt-1 max-w-3xl text-micro text-muted-foreground">Enter sends · Shift+Enter for a new line. She uses the pinned record and her tools.</p>
        </footer>
      </section>
    </div>
    {picker&&<ContextPicker onClose={()=>setPicker(false)} onSelect={next=>{setContext(next);setPicker(false);}}/>}
  </Page>;
}

function ContextPicker({onSelect,onClose}:{onSelect:(c:SelectedContext)=>void;onClose:()=>void}){
  const [kind,setKind]=useState<ContextKind>('opportunity');
  const [query,setQuery]=useState('');
  const [options,setOptions]=useState<Option[]>([]);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState<string|null>(null);

  useEffect(()=>{
    let active=true;
    const timer=window.setTimeout(()=>{
      void (async()=>{
        setLoading(true);setError(null);
        try{
          const h=await authHeaders();
          if(kind==='opportunity'){
            const r=await fetchImpl(`${env.apiBase}/api/opportunities`,{headers:h});
            const b=await r.json().catch(()=>null) as {data?:Record<string,unknown>[]}|null;
            if(!r.ok)throw Error((b as {error?:string}|null)?.error??`Opportunities unavailable (${r.status})`);
            const q=query.trim().toLowerCase();
            const rows=(b?.data??[]).map(o=>({ref:String(o.ref??''),label:String(o.contact_name??o.name??o.customer_name??''),sub:String(o.suburb??''),leadId:o.lead_id?String(o.lead_id):null,jobRef:o.job_ref?String(o.job_ref):null}))
              .filter(o=>o.ref&&(!q||`${o.ref} ${o.label} ${o.sub}`.toLowerCase().includes(q))).slice(0,60);
            if(active)setOptions(rows);
          }else{
            const q=query.trim();
            const r=await fetchImpl(`${env.apiBase}/api/jobs?limit=50${q?`&q=${encodeURIComponent(q)}`:''}`,{headers:h});
            const b=await r.json().catch(()=>null) as {data?:Record<string,unknown>[]}|null;
            if(!r.ok)throw Error((b as {error?:string}|null)?.error??`Jobs unavailable (${r.status})`);
            const rows=(b?.data??[]).map(o=>({ref:String(o.ref??''),label:String(o.name??o.contact_name??''),sub:String(o.suburb??'')})).filter(o=>o.ref).slice(0,60);
            if(active)setOptions(rows);
          }
        }catch(e){if(active)setError(e instanceof Error?e.message:'Could not load records');}
        finally{if(active)setLoading(false);}
      })();
    },250);
    return()=>{active=false;window.clearTimeout(timer);};
  },[kind,query]);

  async function choose(o:Option){
    setError(null);
    let proposalId:string|null=null,proposalTitle:string|null=null;
    try{
      const h=await authHeaders();
      const search=kind==='opportunity'?(o.leadId?`lead_id=${encodeURIComponent(o.leadId)}`:o.jobRef?`job_ref=${encodeURIComponent(o.jobRef)}`:''):`job_ref=${encodeURIComponent(o.ref)}`;
      if(search){
        const r=await fetchImpl(`${env.apiBase}/api/proposal-documents?${search}`,{headers:h});
        const b=await r.json().catch(()=>null) as {data?:{id?:string;content?:{title?:string}}[]}|null;
        const doc=b?.data?.[0];
        if(doc?.id){proposalId=doc.id;proposalTitle=doc.content?.title??null;}
      }
    }catch{/* the record still gives context; the Proposal link is a bonus */}
    onSelect({kind,ref:o.ref,label:[o.label,o.sub].filter(Boolean).join(' · '),proposalId,proposalTitle});
  }

  return createPortal(<div className="fixed inset-0 z-[100] flex items-start justify-center overflow-auto bg-black/40 p-3 md:p-8" role="dialog" aria-modal="true" aria-label="Choose opportunity or job" onKeyDown={e=>{if(e.key==='Escape')onClose();}}>
    <div className="w-full max-w-xl rounded-lg border bg-background shadow-xl">
      <header className="flex items-center justify-between border-b p-4"><div><h2 className="text-lg font-semibold">Choose context</h2><p className="text-sm text-muted-foreground">Pin one opportunity or job to this conversation.</p></div><Button variant="outline" size="sm" onClick={onClose}>Close</Button></header>
      <div className="flex gap-1 border-b px-4 pt-3">{(['opportunity','job'] as const).map(k=><button key={k} onClick={()=>{setKind(k);setQuery('');}} className={`-mb-px border-b-2 px-3 py-2 text-sm ${kind===k?'border-primary font-medium':'border-transparent text-muted-foreground'}`}>{k==='opportunity'?'Opportunity':'Job'}</button>)}</div>
      <div className="p-4">
        <input autoFocus className={`${inputCls} w-full`} value={query} onChange={e=>setQuery(e.target.value)} placeholder={kind==='opportunity'?'Search ref, customer or suburb…':'Search job ref, name or suburb…'}/>
        {error&&<p className="mt-2 text-sm text-red-700" role="alert">{error}</p>}
        <div className="mt-3 max-h-[50vh] overflow-y-auto">
          {loading&&<p className="p-2 text-sm text-muted-foreground">Looking up {kind==='opportunity'?'opportunities':'jobs'}…</p>}
          {!loading&&options.length===0&&<p className="p-2 text-sm text-muted-foreground">No match. Try another ref or name.</p>}
          {options.map(o=><button key={o.ref} onClick={()=>void choose(o)} className="mb-1 block w-full rounded-md border px-3 py-2 text-left text-sm hover:bg-muted"><span className="font-medium">{o.ref}</span>{o.label?` · ${o.label}`:''}{o.sub?<span className="text-muted-foreground"> · {o.sub}</span>:null}</button>)}
        </div>
      </div>
    </div>
  </div>,document.body);
}
